import { useState, useMemo, useCallback, FC, useEffect } from 'react'
import { AxiosResponse } from 'axios'
import { filter } from 'lodash'
import concat from 'lodash/concat'
import uniqBy from 'lodash/uniqBy'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMount } from 'react-use'
import { SHELL_URL } from 'common/constants/env'
import { useAppProvider } from 'components/AppContext'
import { EStatusLead } from 'containers/App/appLayoutEnum'
import { ISeller, ISellerHeader, TMenuItem, TPermission } from 'containers/App/appLayoutTypes'
import { LeadsAccountSellersContext } from 'hooks/useLeadsAccountSellers/constant'
import {
  mergeArrayAccountByLeads,
  processAccountSeller,
  processLeads,
  verifySellerIsDifferent,
} from 'hooks/useLeadsAccountSellers/helpers'
import AccountService from 'services/account/http'
import LeadService from 'services/lead/http'

export const LeadsAccountSellersProvider: FC = ({ children }) => {
  const routerLocation = useLocation()
  const routerNavigate = useNavigate()
  const { runtimeMode } = useAppProvider()

  const [currentSeller, setCurrentSeller] = useState<ISellerHeader | undefined>()
  const [loadingSellers, setLoadingSellers] = useState(true)
  const [sellers, setSellers] = useState<ISellerHeader[]>([])
  const [accountLoading, setAccountLoading] = useState(true)
  const [sellersAccount, setSellersAccount] = useState<ISellerHeader[]>([])
  const [accountMenu, setAccountMenu] = useState<TMenuItem[]>([])
  const [sellerPermissions, setSellerPermissions] = useState<TPermission>()
  const [error, setError] = useState<{ error: boolean; message: string }>({
    error: false,
    message: '',
  })

  const mergedArrayAccountByLeads = useMemo(
    () => mergeArrayAccountByLeads({ sellers, sellersAccount }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sellers), JSON.stringify(sellersAccount)]
  )

  const concatenatedArray = filter(
    uniqBy(concat(mergedArrayAccountByLeads, sellersAccount), 'tenantId'),
    (seller: ISellerHeader) => {
      const statusLead = seller.status_lead ?? EStatusLead.PENDING
      return ![EStatusLead.REPROVED, EStatusLead.QUITTER, EStatusLead.SELLER_INACTIVE].includes(
        statusLead
      )
    }
  )

  const fetchAllData = useCallback(() => {
    Promise.allSettled([
      LeadService.get(),
      AccountService.getSellerPermissions(),
      AccountService.getServices(),
      AccountService.getSeller(),
    ])
      .then(results => {
        const leadsResponse = results[0]

        // Se a requisição de leads falhar, interromper o fluxo e apresentar tela de erro
        if (leadsResponse.status === 'rejected') {
          setError({ error: true, message: 'Erro ao buscar leads' })
          return
        }

        // Filtra requisições bem sucedidas
        const successfulRequests = results
          .filter(
            (result): result is PromiseFulfilledResult<AxiosResponse> =>
              result.status === 'fulfilled'
          )
          .map(result => result.value)

        // Processa requisições bem sucedidas
        successfulRequests.forEach(req => {
          switch (req.config.url) {
            case '/leads':
              setSellers(processLeads(req.data as ISeller[]))
              break
            case '/v2/auth/permissions':
              setSellerPermissions(req.data as TPermission)

              break
            case `${SHELL_URL}menu.json`:
              setAccountMenu(req.data as TMenuItem[])
              break
            case '/v2/auth':
              sessionStorage.setItem('sso_id', req.data?.ssoId)
              setSellersAccount(processAccountSeller(req.data))
              break
            default:
              break
          }
        })
      })

      .finally(() => {
        setLoadingSellers(false)
        setAccountLoading(false)
      })
  }, [])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (loadingSellers || accountLoading) {
      return
    }

    verifySellerIsDifferent(concatenatedArray, setCurrentSeller)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(concatenatedArray), loadingSellers, accountLoading])

  useMount(() => {
    const params = new URLSearchParams(routerLocation.search)
    const searchParamLeadId = params.get('leadId')
    const searchParamTenantId = params.get('tenantId')
    const maasTenantId = sessionStorage.getItem('maas-tenantId')

    const selectedSeller = concatenatedArray.find(
      seller =>
        searchParamLeadId === seller.id ||
        [searchParamTenantId, maasTenantId].includes(seller.seller_id)
    )

    params.delete('leadId')
    params.delete('tenantId')

    if (runtimeMode === 'default' && params.toString()) {
      routerNavigate(
        {
          search: `?${params.toString()}`,
        },
        { replace: true }
      )
    }

    if (searchParamLeadId) {
      sessionStorage.setItem('maas-leadId', searchParamLeadId)
    }

    if (selectedSeller) {
      setCurrentSeller(selectedSeller)
      return sessionStorage.setItem('maas-tenantId', selectedSeller.seller_id)
    }
  })

  const contextValue = useMemo(
    () => ({
      sellers,
      loadingSellers,
      error,
      setError,
      concatenatedArray,
      setLoadingSellers,
      accountLoading,
      sellerPermissions,
      accountMenu,
      sellersAccount,
      fetchAllData,
      setAccountLoading,
      currentSeller,
      setCurrentSeller,
    }),
    [
      accountLoading,
      accountMenu,
      concatenatedArray,
      currentSeller,
      error,
      fetchAllData,
      loadingSellers,
      sellerPermissions,
      sellers,
      sellersAccount,
    ]
  )

  return (
    <LeadsAccountSellersContext.Provider value={contextValue}>
      {children}
    </LeadsAccountSellersContext.Provider>
  )
}
